import React, { useState, useEffect, useRef } from 'react';
import DiagnosticTopicsModal from './DiagnosticTopicsModal';
import { useNetworkPicker } from '../NetworkPicker/NetworkPickerModal';
import {
  TOPIC_TYPES,
  normalizeTopic,
  validateTopic,
  findTopic,
  findMainTopic,
  addTopic,
  updateTopic,
  replaceTopic,
  removeTopic,
  ensureSingleMainTopic,
  removeDuplicateTopics
} from './topicHelpers';
import { getTokenForRequest } from '../../utils/apiAuth';
import './QuestionTopicSelection.css';

/**
 * QuestionTopicSelection Component
 * React replacement for Angular questionTopicSelection directive
 * Handles topic selection (main and diagnostic) for questions
 */
const QuestionTopicSelection = ({ questionInstance, containerId, onTopicsChange }) => {
  const [question, setQuestion] = useState(questionInstance || { Topics: [] });
  const [showDiagnosticModal, setShowDiagnosticModal] = useState(false);
  const [selectedMainTopic, setSelectedMainTopic] = useState(null);
  const [isMainTopicMode, setIsMainTopicMode] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const questionRef = useRef(question);
  const isMainTopicModeRef = useRef(isMainTopicMode);
  const isOpeningModalRef = useRef(false);
  const diagnosticModalProtectedRef = useRef(false);

  // Keep question ref in sync
  useEffect(() => {
    questionRef.current = question;
  }, [question]);

  // Keep isMainTopicMode ref in sync to avoid closure issues
  useEffect(() => {
    isMainTopicModeRef.current = isMainTopicMode;
  }, [isMainTopicMode]);

  // Update question when questionInstance prop changes
  useEffect(() => {
    if (questionInstance) {
      // Remove any duplicate topics from incoming data
      const cleanedTopics = removeDuplicateTopics(questionInstance.Topics || []);
      setQuestion({ ...questionInstance, Topics: cleanedTopics });
    } else {
      setQuestion({ Topics: [] });
    }
  }, [questionInstance]);

  // Notify parent of topic changes
  useEffect(() => {
    if (onTopicsChange) {
      onTopicsChange(question);
    }
  }, [question, onTopicsChange]);

  const { openNetworkPicker, NetworkPicker } = useNetworkPicker();
  
  // Reset modal opening flag when network picker closes
  useEffect(() => {
    // This effect will help ensure the flag is reset if modal closes unexpectedly
    if (!isLoading && isOpeningModalRef.current) {
      // Check if modal is actually closed
      if (typeof $ !== 'undefined') {
        const modal = $('.network-picker-modal');
        if (modal.length === 0 || (!modal.is(':visible') && !modal.hasClass('show'))) {
          isOpeningModalRef.current = false;
        }
      }
    }
  }, [isLoading]);

  /**
   * Clean up network picker modal
   */
  const cleanupNetworkModal = () => {
    if (typeof $ === 'undefined') return;

    // Only close network picker modal, exclude diagnostic topics modal
    const networkModal = $('.network-picker-modal');
    if (networkModal.length > 0) {
      networkModal.modal('hide');
    }
    
    // Close other jQuery modals, but exclude the diagnostic topics modal (React-controlled)
    const diagnosticModal = $('#chooseDiagnosticeTopics');
    const otherModals = $('.modal').not('#chooseDiagnosticeTopics');
    if (otherModals.length > 0) {
      otherModals.modal('hide');
    }
    
    // If in diagnostic mode, ensure diagnostic modal is closed and won't show
    // Use ref to get current value to avoid closure issues
    if (!isMainTopicModeRef.current) {
      setShowDiagnosticModal(false);
      setSelectedMainTopic(null);
      // Also hide the modal in DOM if it exists
      if (diagnosticModal.length > 0) {
        diagnosticModal.hide();
      }
    }
    
    const cleanup = () => {
      // Check if diagnostic topics modal should be showing
      // Only show if in main topic mode AND modal is actually visible in DOM
      // Use ref to get current value to avoid closure issues
      const shouldShowDiagnosticModal = isMainTopicModeRef.current && 
                                        (diagnosticModalProtectedRef.current || 
                                         (diagnosticModal.length > 0 && diagnosticModal.is(':visible')));
      
      // If in diagnostic mode, always ensure diagnostic modal is closed
      // Use ref to get current value to avoid closure issues
      if (!isMainTopicModeRef.current) {
        // Remove all backdrops when in diagnostic mode
        $('.modal-backdrop').remove();
      } else if (!shouldShowDiagnosticModal) {
        // In main topic mode but modal not showing, remove backdrops
        $('.modal-backdrop').remove();
      } else {
        // In main topic mode and modal is showing, keep its backdrop
        $('.modal-backdrop').each(function() {
          const $backdrop = $(this);
          // Check if this backdrop is for the diagnostic topics modal
          const isDiagnosticBackdrop = $backdrop.next('#chooseDiagnosticeTopics').length > 0 ||
                                       $backdrop.siblings('#chooseDiagnosticeTopics').length > 0 ||
                                       $backdrop.prev('#chooseDiagnosticeTopics').length > 0;
          if (!isDiagnosticBackdrop) {
            $backdrop.remove();
          }
        });
      }
      
      $('.fade').filter(function() {
        return $(this).css('position') === 'fixed' && 
               $(this).css('z-index') > 1000 &&
               $(this).attr('id') !== 'chooseDiagnosticeTopics';
      }).remove();
      
      // Only remove modal-open class if diagnostic topics modal should not be showing
      if (!shouldShowDiagnosticModal) {
        $('body').removeClass('modal-open');
        $('body').css({
          'padding-right': '',
          'overflow': '',
          'position': ''
        });
        
        const bodyStyle = $('body').attr('style');
        if (bodyStyle && (bodyStyle.includes('padding-right') || bodyStyle.includes('overflow'))) {
          $('body').removeAttr('style');
        }
      }
      
      // Remove other backdrops/overlays, but not for diagnostic topics modal if it should be showing
      if (!shouldShowDiagnosticModal) {
        $('[class*="backdrop"], [class*="overlay"]').filter(function() {
          return $(this).css('position') === 'fixed' && 
                 ($(this).css('z-index') > 1000 || $(this).hasClass('modal-backdrop'));
        }).remove();
      }
    };
    
    cleanup();
    setTimeout(cleanup, 100);
    setTimeout(cleanup, 300);
    setTimeout(cleanup, 500);
  };

  /**
   * Convert diagnostic topic to main topic (immutable)
   */
  const convertDiagnosticToMain = (topics, topicId, newMainTopic) => {
    // Update the diagnostic topic to main
    let updatedTopics = updateTopic(topics, topicId, { IsMain: TOPIC_TYPES.MAIN, Desc: newMainTopic.Desc });
    
    // Ensure only one main topic exists
    updatedTopics = ensureSingleMainTopic(updatedTopics, topicId);
    
    return updatedTopics;
  };

  /**
   * Convert main topic to diagnostic (immutable)
   */
  const convertMainToDiagnostic = (topics, topicId) => {
    return updateTopic(topics, topicId, { IsMain: TOPIC_TYPES.DIAGNOSTIC });
  };

  /**
   * Handle existing topic scenarios
   */
  const handleExistingTopic = async (topic, existingTopic, topics, isMainMode) => {
    const validation = validateTopic(topic);
    if (!validation.valid) {
      const errorMsg = validation.errors.join(', ');
      if (window.modalMessage) {
        window.modalMessage('Validation Error', errorMsg);
      } else {
        alert(`Validation Error: ${errorMsg}`);
      }
      return topics;
    }

    if (isMainMode) {
      // Main topic mode: ensure only one main topic exists
      // Always remove any diagnostic topics with the same ID when adding as main topic
      const normalizedTopic = normalizeTopic(topic, TOPIC_TYPES.MAIN);
      const existingMainTopic = findMainTopic(topics);
      
      let updatedTopics;
      
      // First, remove any diagnostic topics with the same ID (if topic exists as diagnostic, remove it)
      // This ensures that if a topic is in the diagnostic list, it gets removed when selected as main
      updatedTopics = topics.filter(t => !(t.ID === topic.ID && t.IsMain !== TOPIC_TYPES.MAIN));
      
      // If the existing topic is a diagnostic topic, remove it from diagnostic list and make it main
      if (existingTopic.IsMain !== TOPIC_TYPES.MAIN) {
        // Topic exists as diagnostic - already removed above, now add/replace as main topic
        if (existingMainTopic) {
          // Replace existing main topic with this topic (prevent duplicate main topics)
          updatedTopics = replaceTopic(updatedTopics, existingMainTopic.ID, normalizedTopic);
        } else {
          // Add as new main topic
          updatedTopics = addTopic(updatedTopics, normalizedTopic);
        }
        // Ensure only one main topic exists (safeguard - prevent duplicates)
        updatedTopics = ensureSingleMainTopic(updatedTopics, topic.ID);
      } else if (existingMainTopic && existingMainTopic.ID !== topic.ID) {
        // Topic is already main, but there's a different main topic - replace it (prevent duplicate main topics)
        // Diagnostic topics with same ID already removed above
        updatedTopics = replaceTopic(updatedTopics, existingMainTopic.ID, normalizedTopic);
        // Ensure only one main topic exists (safeguard - prevent duplicates)
        updatedTopics = ensureSingleMainTopic(updatedTopics, topic.ID);
      } else {
        // Topic is already the main topic, no change needed (prevent duplicate)
        // But still remove any diagnostic topics with same ID (already done above)
        // Still ensure only one main topic exists (safeguard - prevent duplicates)
        updatedTopics = ensureSingleMainTopic(updatedTopics, topic.ID);
      }
      
      // Auto-show diagnostic modal when main topic is selected (main topic button only)
      // ONLY if in main topic mode - pass isMainMode to avoid closure issues
      if (isMainMode) {
        await promptForDiagnosticTopics(topic, updatedTopics, isMainMode);
      }
      return updatedTopics;
    } else {
      // Diagnostic topic mode - never show diagnostic modal, just add directly
      // NO CHECK for children - just add the topic to diagnostic list
      // Ensure any existing diagnostic modal is closed
      setShowDiagnosticModal(false);
      setSelectedMainTopic(null);
      
      if (existingTopic.IsMain === TOPIC_TYPES.MAIN) {
        // If the selected topic is currently in the main topic list:
        // 1. Remove it from main topic list (convert IsMain from true to false)
        // 2. Add it to diagnostic topic list (IsMain = false means it's diagnostic)
        // This ensures main topic list only has one topic (or none if converted)
        return convertMainToDiagnostic(topics, topic.ID);
      } else {
        // If topic already exists as diagnostic, don't add it again (prevent duplicates)
        // Check if there are multiple instances and remove duplicates
        const diagnosticCount = topics.filter(t => t.ID === topic.ID && t.IsMain !== TOPIC_TYPES.MAIN).length;
        if (diagnosticCount > 0) {
          // Topic already exists as diagnostic - prevent duplicates
          return topics; // Topic already exists as diagnostic, return unchanged
        }
        // Should not reach here if existingTopic exists, but safety check
        return topics;
      }
    }
  };

  /**
   * Handle new topic scenarios
   */
  const handleNewTopic = async (topic, topics, isMainMode) => {
    const validation = validateTopic(topic);
    if (!validation.valid) {
      const errorMsg = validation.errors.join(', ');
      if (window.modalMessage) {
        window.modalMessage('Validation Error', errorMsg);
      } else {
        alert(`Validation Error: ${errorMsg}`);
      }
      return topics;
    }

    if (isMainMode) {
      // Adding main topic: main topic list can only have ONE main topic (no duplicates)
      const normalizedTopic = normalizeTopic(topic, TOPIC_TYPES.MAIN);
      const existingMainTopic = findMainTopic(topics);
      
      let updatedTopics;
      
      // First, remove any diagnostic topics with the same ID (if topic exists as diagnostic, remove it)
      // This ensures that if a topic is in the diagnostic list, it gets removed when selected as main
      updatedTopics = topics.filter(t => !(t.ID === topic.ID && t.IsMain !== TOPIC_TYPES.MAIN));
      
      // Check if this topic already exists as main topic to prevent duplicates
      const existingMainTopicWithSameId = updatedTopics.find(t => t.ID === topic.ID && t.IsMain === TOPIC_TYPES.MAIN);
      if (existingMainTopicWithSameId) {
        // Topic already exists as main topic - no change needed (prevent duplicate)
        // updatedTopics already has diagnostic topics removed
      } else if (existingMainTopic) {
        // Replace existing main topic, keep all diagnostic topics (except the one we're converting)
        // This ensures only one main topic exists in the main topic list (prevent duplicate)
        updatedTopics = replaceTopic(updatedTopics, existingMainTopic.ID, normalizedTopic);
      } else {
        // Add new main topic, keep all existing diagnostic topics (except the one we're converting)
        updatedTopics = addTopic(updatedTopics, normalizedTopic);
      }
      
      // Ensure only one main topic exists (safeguard - prevent duplicates)
      updatedTopics = ensureSingleMainTopic(updatedTopics, topic.ID);
      
      // Auto-show diagnostic modal when main topic is selected (main topic button only)
      // ONLY if in main topic mode - pass isMainMode to avoid closure issues
      if (isMainMode) {
        await promptForDiagnosticTopics(topic, updatedTopics, isMainMode);
      }
      return updatedTopics;
    } else {
      // Adding diagnostic topic: just add to diagnostic topic list
      // NO CHECK for children, NO CHECK for main topic relationship, no warnings
      // Ensure any existing diagnostic modal is closed - we never show modal for diagnostic topic selection
      setShowDiagnosticModal(false);
      setSelectedMainTopic(null);
      
      // Check if topic already exists as diagnostic to prevent duplicates
      const existingDiagnosticTopic = topics.find(t => t.ID === topic.ID && t.IsMain !== TOPIC_TYPES.MAIN);
      if (existingDiagnosticTopic) {
        // Topic already exists as diagnostic - prevent duplicates
        return topics; // Return unchanged, no duplicate added
      }
      
      // Topic doesn't exist as diagnostic, add it
      const normalizedTopic = normalizeTopic(topic, TOPIC_TYPES.DIAGNOSTIC);
      return addTopic(topics, normalizedTopic);
    }
  };

  /**
   * Handle topic selection from network picker
   */
  const handleTopicSelected = async (topic) => {
    // Reset modal opening flag
    isOpeningModalRef.current = false;
    
    if (!topic) {
      console.warn('No topic provided to handleTopicSelected');
      setIsLoading(false); // Ensure loading is reset
      return;
    }

    try {
      setIsLoading(true);
      // Delay cleanup slightly to avoid interfering with modal opening
      // Only cleanup if diagnostic modal is not protected
      setTimeout(() => {
        if (!diagnosticModalProtectedRef.current) {
          cleanupNetworkModal();
        }
      }, 100);

      // Get current mode state - use ref to avoid closure issues
      const currentMainTopicMode = isMainTopicModeRef.current;
      
      // If in diagnostic mode, ensure diagnostic modal is closed (we never show modal for diagnostic topic selection)
      if (!currentMainTopicMode) {
        setShowDiagnosticModal(false);
        setSelectedMainTopic(null);
      }

      const currentQuestion = questionRef.current;
      const currentTopics = currentQuestion.Topics || [];

      // Check if topic already exists
      const existingTopic = findTopic(currentTopics, topic.ID);
      
      let updatedTopics;
      if (existingTopic) {
        updatedTopics = await handleExistingTopic(topic, existingTopic, currentTopics, currentMainTopicMode);
      } else {
        updatedTopics = await handleNewTopic(topic, currentTopics, currentMainTopicMode);
      }

      // Double-check: if we're in diagnostic mode, ensure modal is still closed
      if (!currentMainTopicMode) {
        setShowDiagnosticModal(false);
        setSelectedMainTopic(null);
      }

      // Remove any duplicate topics before updating
      updatedTopics = removeDuplicateTopics(updatedTopics);
      setQuestion({ ...currentQuestion, Topics: updatedTopics });
    } catch (error) {
      console.error('Error handling topic selection:', error);
      if (window.modalMessage) {
        window.modalMessage('Error', `Failed to add topic: ${error.message}`);
      } else {
        alert(`Failed to add topic: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Manually prompt user to select diagnostic topics for a main topic
   * This is a MANUAL function - does NOT auto-show after selecting main topic
   * ONLY works when in main topic mode - NEVER shows in diagnostic mode
   * @param {Object} mainTopic - The main topic to get diagnostic topics for
   * @param {Array} currentTopicsList - Current topics list (optional)
   * @param {boolean} isMainMode - Current mode (true = main topic mode, false = diagnostic mode)
   */
  const promptForDiagnosticTopics = async (mainTopic, currentTopicsList = null, isMainMode = true) => {
    // Safety check: NEVER show modal or check for children if we're in diagnostic mode
    // Also check the current state to be extra safe - use ref to get current value
    if (!isMainMode || !isMainTopicModeRef.current) {
      console.warn('promptForDiagnosticTopics called in diagnostic mode - ignoring, no child check, no API call');
      return;
    }
    
    try {
      setIsLoading(true);
      const tokenForRequest = getTokenForRequest();
      const encodedToken = window.encodeToken ? window.encodeToken(tokenForRequest) : tokenForRequest;

      const response = await fetch(`./webservices/Question.svc/TestedTopic?token=${encodedToken}&nodeID=${mainTopic.ID}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to load diagnostic topics');
      }

      const result = await response.json();

      // Triple-check we're still in main topic mode before showing modal
      // Check both the parameter and the current state - use ref to get current value
      if (!isMainMode || !isMainTopicModeRef.current) {
        console.warn('Mode changed to diagnostic before showing modal - aborting');
        return;
      }

      // Check if there are child topics - ONLY if in main topic mode
      if ((result.FirstLayerChildren && result.FirstLayerChildren.length > 0) || 
          (result.SecondLayerChildren && result.SecondLayerChildren.length > 0)) {
        
        // Use provided topics list or fall back to ref (for backward compatibility)
        const currentTopics = currentTopicsList || questionRef.current.Topics || [];
        // Mark existing topics as selected (only if they're not main topics)
        const firstLayer = (result.FirstLayerChildren || []).map(topic => ({
          ...topic,
          selected: currentTopics.some(t => t.ID === topic.ID && t.IsMain !== TOPIC_TYPES.MAIN)
        }));
        const secondLayer = (result.SecondLayerChildren || []).map(topic => ({
          ...topic,
          selected: currentTopics.some(t => t.ID === topic.ID && t.IsMain !== TOPIC_TYPES.MAIN)
        }));

        setSelectedMainTopic({
          ...mainTopic,
          FirstLayerChildren: firstLayer,
          SecondLayerChildren: secondLayer
        });
        // Protect diagnostic modal from cleanup
        diagnosticModalProtectedRef.current = true;
        setShowDiagnosticModal(true);
        
        // Remove protection after modal is fully shown (give it time to render)
        setTimeout(() => {
          diagnosticModalProtectedRef.current = false;
        }, 500);
      }
    } catch (error) {
      console.error('Error loading diagnostic topics:', error);
      if (window.modalMessage) {
        window.modalMessage('Failed', 'An error occurred in getting diagnostics topic.');
      } else {
        alert('An error occurred in getting diagnostics topic.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Process diagnostic topics layer (immutable)
   * IMPORTANT: Never modify or remove the main topic - it must be preserved
   * IMPORTANT: Prevent duplicate diagnostic topics
   */
  const processDiagnosticLayer = (topics, layerChildren) => {
    let updatedTopics = [...topics];
    const mainTopic = findMainTopic(updatedTopics);

    layerChildren.forEach(topic => {
      // Check if topic already exists as diagnostic (prevent duplicates)
      const existingDiagnosticTopics = updatedTopics.filter(t => t.ID === topic.ID && t.IsMain !== TOPIC_TYPES.MAIN);
      const existingTopic = findTopic(updatedTopics, topic.ID);
      
      // NEVER modify the main topic - if this topic is the main topic, skip it
      if (mainTopic && existingTopic && existingTopic.ID === mainTopic.ID) {
        return; // Skip processing the main topic
      }
      
      if (topic.selected) {
        // Check if topic already exists as diagnostic to prevent duplicates
        if (existingDiagnosticTopics.length > 0) {
          // Topic already exists as diagnostic - update the first one, don't add duplicate
          const firstDiagnostic = existingDiagnosticTopics[0];
          updatedTopics = updateTopic(updatedTopics, firstDiagnostic.ID, {
            IsMain: TOPIC_TYPES.DIAGNOSTIC,
            Desc: topic.Desc || topic.Name || firstDiagnostic.Desc
          });
          // Remove any duplicate diagnostic topics with the same ID
          updatedTopics = updatedTopics.filter((t, index, self) => {
            if (t.ID === topic.ID && t.IsMain !== TOPIC_TYPES.MAIN) {
              // Keep only the first occurrence
              return index === self.findIndex(item => item.ID === t.ID && item.IsMain !== TOPIC_TYPES.MAIN);
            }
            return true;
          });
        } else if (existingTopic && existingTopic.IsMain === TOPIC_TYPES.MAIN) {
          // Topic exists as main topic - don't modify it, skip
          return;
        } else if (!existingTopic) {
          // Add new diagnostic topic (doesn't exist yet)
          const normalizedTopic = normalizeTopic(topic, TOPIC_TYPES.DIAGNOSTIC);
          updatedTopics = addTopic(updatedTopics, normalizedTopic);
        }
      } else {
        // Remove if exists, but ONLY if it's NOT the main topic
        // Remove all diagnostic topics with this ID (remove duplicates)
        if (existingDiagnosticTopics.length > 0) {
          updatedTopics = updatedTopics.filter(t => !(t.ID === topic.ID && t.IsMain !== TOPIC_TYPES.MAIN));
        }
      }
    });

    return updatedTopics;
  };

  /**
   * Handle adding selected diagnostic topics
   */
  const handleAddDiagnosticTopics = (firstLayerChildren, secondLayerChildren) => {
    const currentQuestion = questionRef.current;
    let updatedTopics = [...(currentQuestion.Topics || [])];

    // Process first layer children
    updatedTopics = processDiagnosticLayer(updatedTopics, firstLayerChildren);

    // Process second layer children
    updatedTopics = processDiagnosticLayer(updatedTopics, secondLayerChildren);

    // Ensure main topic is preserved
    const mainTopic = findMainTopic(updatedTopics);
    if (!mainTopic && selectedMainTopic) {
      // Main topic was accidentally removed, restore it
      const normalizedMainTopic = normalizeTopic(selectedMainTopic, TOPIC_TYPES.MAIN);
      updatedTopics = addTopic(updatedTopics, normalizedMainTopic);
    }

    // Remove any duplicate topics before updating
    updatedTopics = removeDuplicateTopics(updatedTopics);
    setQuestion({ ...currentQuestion, Topics: updatedTopics });
    diagnosticModalProtectedRef.current = false;
    setShowDiagnosticModal(false);
    setSelectedMainTopic(null);
  };

  /**
   * Handle removing a topic (immutable)
   * @param {string|number} topicId - Topic ID to remove
   * @param {boolean} isMain - Whether the topic is a main topic
   * @param {Event} event - Click event
   */
  const handleRemoveTopic = (topicId, isMain, event) => {
    if (event) {
      event.stopPropagation();
    }

    const currentQuestion = questionRef.current;
    const topics = currentQuestion.Topics || [];
    
    if (!topicId) {
      console.warn('Cannot remove topic: topicId is required');
      return;
    }

    // Find the topic to remove
    const topicToRemove = findTopic(topics, topicId);
    if (!topicToRemove) {
      console.warn(`Topic with ID ${topicId} not found`);
      return;
    }

    // Remove topic from list by ID (main and diagnostic both removed from Topics)
    let updatedTopics = removeTopic(topics, topicId);
    updatedTopics = removeDuplicateTopics(updatedTopics);
    setQuestion({ ...currentQuestion, Topics: updatedTopics });
  };

  /**
   * Handle link main topic button click
   */
  const handleLinkMainTopic = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Prevent multiple simultaneous opens
    if (isOpeningModalRef.current) {
      console.warn('Link Main Topic blocked: modal is already opening');
      return;
    }
    
    if (isLoading) {
      console.warn('Link Main Topic blocked: isLoading is true');
      return;
    }
    
    console.log('Link Main Topic clicked');
    isOpeningModalRef.current = true;
    setIsMainTopicMode(true);
    
    // Use setTimeout to ensure state is updated before opening modal
    setTimeout(() => {
      try {
        openNetworkPicker(handleTopicSelected);
        console.log('openNetworkPicker called');
        // Reset the flag after a short delay to allow modal to initialize
        setTimeout(() => {
          isOpeningModalRef.current = false;
        }, 500);
      } catch (error) {
        console.error('Error opening network picker:', error);
        isOpeningModalRef.current = false;
        setIsLoading(false); // Reset loading state on error
        if (window.modalMessage) {
          window.modalMessage('Error', 'Failed to open topic selector.');
        } else {
          alert('Failed to open topic selector.');
        }
      }
    }, 0);
  };

  /**
   * Handle link diagnostic topic button click
   */
  const handleLinkDiagnosticTopic = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Prevent multiple simultaneous opens
    if (isOpeningModalRef.current) {
      console.warn('Link Diagnostic Topic blocked: modal is already opening');
      return;
    }
    
    if (isLoading) {
      console.warn('Link Diagnostic Topic blocked: isLoading is true');
      return;
    }
    
    console.log('Link Diagnostic Topic clicked');
    isOpeningModalRef.current = true;
    setIsMainTopicMode(false);
    
    // Use setTimeout to ensure state is updated before opening modal
    setTimeout(() => {
      try {
        openNetworkPicker(handleTopicSelected);
        console.log('openNetworkPicker called');
        // Reset the flag after a short delay to allow modal to initialize
        setTimeout(() => {
          isOpeningModalRef.current = false;
        }, 500);
      } catch (error) {
        console.error('Error opening network picker:', error);
        isOpeningModalRef.current = false;
        setIsLoading(false); // Reset loading state on error
        if (window.modalMessage) {
          window.modalMessage('Error', 'Failed to open topic selector.');
        } else {
          alert('Failed to open topic selector.');
        }
      }
    }, 0);
  };

  const mainTopics = (question.Topics || []).filter(t => t.IsMain);
  const diagnosticTopics = (question.Topics || []).filter(t => !t.IsMain);

  return (
    <div className="question-topic-selection">
      <NetworkPicker />
      
      {/* Main Topic Section */}
      <div className="mainTopic">
        <div className="add-mcq-main-header">Tested Topic :</div>
        <div className="add-mcq-chosen-topics">
          {mainTopics.map((topic, index) => {
            return (
              <div 
                key={topic.ID || index}
                className="add-mcq-main-topic mainTopic"
                topicID={topic.ID}
                topicName={topic.Desc}
                topicIsMain={topic.IsMain ? "true" : "false"}
              >
                {topic.Desc} 
                <span 
                  className="remove-topic" 
                  onClick={(e) => handleRemoveTopic(topic.ID, true, e)}
                >
                  {' '}X{' '}
                </span>
              </div>
            );
          })}
          <div 
            className="btn btn-success add-mcq-topics network-picker-btn" 
            onClick={handleLinkMainTopic}
            style={{ 
              cursor: isLoading ? 'not-allowed' : 'pointer', 
              opacity: isLoading ? 0.6 : 1,
              pointerEvents: isLoading ? 'none' : 'auto'
            }}
            role="button"
            tabIndex={isLoading ? -1 : 0}
            onKeyDown={(e) => {
              if (!isLoading && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                handleLinkMainTopic(e);
              }
            }}
          >
            {isLoading ? 'Loading...' : 'Link Main Topic'}
          </div>
        </div>
      </div>

      {/* Diagnostic Topics Section */}
      <div className="diagnosticsTopics">
        <div className="add-mcq-main-header">Diagnostic Topics :</div>
        <div className="add-mcq-chosen-topics">
          {diagnosticTopics.map((topic, index) => {
            return (
              <div 
                key={topic.ID || index}
                className="diagnosticsTopic add-mcq-diagnostic-topic add-mcq-main-topic"
                topicID={topic.ID}
                topicName={topic.Desc}
                topicIsMain={topic.IsMain ? "true" : "false"}
              >
                {topic.Desc} 
                <span 
                  className="remove-topic" 
                  onClick={(e) => handleRemoveTopic(topic.ID, false, e)}
                >
                  {' '}X{' '}
                </span>
              </div>
            );
          })}
          <div 
            className="btn btn-success add-mcq-topics network-picker-btn" 
            onClick={handleLinkDiagnosticTopic}
            style={{ 
              cursor: isLoading ? 'not-allowed' : 'pointer', 
              opacity: isLoading ? 0.6 : 1,
              pointerEvents: isLoading ? 'none' : 'auto'
            }}
            role="button"
            tabIndex={isLoading ? -1 : 0}
            onKeyDown={(e) => {
              if (!isLoading && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                handleLinkDiagnosticTopic(e);
              }
            }}
          >
            {isLoading ? 'Loading...' : 'Link Diagnostic Topic'}
          </div>
        </div>
      </div>

      {/* Diagnostic Topics Modal */}
      {showDiagnosticModal && selectedMainTopic && (
        <DiagnosticTopicsModal
          show={showDiagnosticModal}
          onHide={() => {
            diagnosticModalProtectedRef.current = false;
            setShowDiagnosticModal(false);
            setSelectedMainTopic(null);
          }}
          onSkip={() => {
            diagnosticModalProtectedRef.current = false;
            setShowDiagnosticModal(false);
            setSelectedMainTopic(null);
          }}
          mainTopic={selectedMainTopic}
          currentTopics={question.Topics || []}
          onSave={handleAddDiagnosticTopics}
        />
      )}
    </div>
  );
};

export default QuestionTopicSelection;

